import React, { useEffect, useState, useCallback } from 'react';

/* ═══════ SOURCE TOOLS (LEFT), Scattered ═══════ */
const sources = [
  { label: 'Salesforce', delay: 0, x: 115, y: 75 },
  { label: 'Gong', delay: 150, x: 185, y: 75 },
  { label: 'Gmail', delay: 300, x: 115, y: 145 },
  { label: 'Slack', delay: 450, x: 185, y: 145 },
  { label: 'Zoho', delay: 600, x: 150, y: 210 },
];

/* ═══════ OUTPUT ACTIONS ═══════ */
const actions = [
  { role: 'AEs / Growth', label: 'Send proposal to Delta', color: '#3B82F6' },
  { role: 'Customer Success', label: 'Intervene: churn at Globex', color: '#10B981' },
];

/* ═══════ ROTATING CRM UPDATES ═══════ */
const crmUpdates = [
  { crm: 'Salesforce' },
  { crm: 'Jira' },
  { crm: 'Salesforce' },
  { crm: 'Jira' },
];

/* ═══════ BRAND LOGOS ═══════ */
function SalesforceLogo() {
  return <g />;
}
function GongLogo() { return <g />; }
function GmailLogo() { return <g />; }
function SlackLogo() { return <g />; }
function ZohoLogo() { return <g />; }
const logoComponents = [SalesforceLogo, GongLogo, GmailLogo, SlackLogo, ZohoLogo];

/* ═══════ ANIMATED DOT STREAM (with organic scatter, speed scales with distance) ═══════ */
function DotStream({ fromX, fromY, toX, toY, active, color = '#CBD5E1', count = 5 }) {
  if (!active) return null;
  const dots = [];
  const scatterY = [-6, 4, -3, 7, -5, 2, -8, 5];
  const scatterX = [2, -3, 5, -2, 4, -4, 1, -1];
  // Scale duration to distance so speed feels consistent
  const dist = Math.sqrt((toX - fromX) ** 2 + (toY - fromY) ** 2);
  const dur = Math.max(0.6, Math.min(1.6, dist / 150));
  for (let i = 0; i < count; i++) {
    const delay = i * (dur * 0.13);
    const sy = scatterY[i % scatterY.length];
    const sx = scatterX[i % scatterX.length];
    const midX = fromX + (toX - fromX) * 0.5 + sx;
    const midY = fromY + (toY - fromY) * 0.5 + sy;
    dots.push(
      <circle key={i} r={3 + (i % 2)} fill={color} opacity="0">
        <animate attributeName="cx" values={`${fromX};${midX};${toX}`} dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" />
        <animate attributeName="cy" values={`${fromY};${midY};${toY}`} dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" />
        <animate attributeName="opacity" values="0;0.85;0.85;0" dur={`${dur}s`} begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
    );
  }
  return <g>{dots}</g>;
}

/* ═══════ ANIMATION PHASES ═══════ */
// Phase 0: tools → forsyt (all tools light up sequentially)
// Phase 1: forsyt → action card 0 (AE)
// Phase 2: action card 0 → CRM (do it for me)
// Phase 3: forsyt → action card 1 (CS)
// Phase 4: action card 1 → CRM (do it for me)
// Then loop

const PHASE_DURATION = 1800; // ms per phase
const TOTAL_PHASES = 5;

const DataFlowAnimation = () => {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState(0);
  const [crmIndex, setCrmIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((prev) => {
        const next = (prev + 1) % TOTAL_PHASES;
        // Update CRM on phases 2 and 4 (when action reaches CRM)
        if (next === 2 || next === 4) {
          setCrmIndex((ci) => (ci + 1) % crmUpdates.length);
        }
        return next;
      });
    }, PHASE_DURATION);
    return () => clearInterval(interval);
  }, []);

  const svgW = 820;
  const svgH = 270;
  const centerX = 324;
  const centerY = svgH / 2;
  const outCardX = 439;
  const actionYs = [72, 158];
  const crmX = 706;

  const currentCrm = crmUpdates[crmIndex];

  // Which elements are active per phase
  const toolsActive = phase === 0;
  const action0Active = phase === 1 || phase === 2;
  const action1Active = phase === 3 || phase === 4;
  const crm0Active = phase === 2;
  const crm1Active = phase === 4;

  return (
    <div
      className={`relative w-full max-w-5xl mx-auto mt-8 transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      data-testid="data-flow-animation"
    >
      {/* ═══════ MOBILE: simplified vertical flow (< md) ═══════ */}
      <div className="md:hidden px-4" data-testid="data-flow-mobile">
        <div className="flex flex-col items-center gap-5">
          {/* Tool chips row */}
          <div className="w-full">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest text-center mb-3">Connects to</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {/* Salesforce */}
              <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24">
                  <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
                </svg>
              </div>
              <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                <img src="/assets/dcxyztic.png" alt="Gong" className="w-6 h-6 object-contain" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                <img src="/assets/0hye7w5y.png" alt="Gmail" className="w-6 h-6 object-contain" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                <img src="/assets/yi9s0mbf.webp" alt="Slack" className="w-6 h-6 object-contain" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                <img src="/assets/sbre7usi.png" alt="Jira" className="w-6 h-6 object-contain" />
              </div>
            </div>
          </div>

          {/* Animated down chevron */}
          <div className="flex flex-col items-center">
            <div className="w-px h-6 bg-gradient-to-b from-gray-300 to-blue-400" />
            <div className="w-2 h-2 rotate-45 border-r-2 border-b-2 border-blue-500 -mt-1" />
          </div>

          {/* Forsyt center */}
          <div className="relative">
            <div className="absolute inset-0 w-20 h-20 -m-2 rounded-full border border-blue-200/50 animate-pulse" />
            <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
              <img
                src="/assets/dcztovt1.png"
                alt="Forsyt"
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>

          {/* Down chevron */}
          <div className="flex flex-col items-center">
            <div className="w-px h-6 bg-gradient-to-b from-blue-400 to-gray-300" />
            <div className="w-2 h-2 rotate-45 border-r-2 border-b-2 border-blue-500 -mt-1" />
          </div>

          {/* Action cards, stacked */}
          <div className="w-full max-w-sm space-y-2">
            {actions.map((action, i) => {
              const isActive = (i === 0 && action0Active) || (i === 1 && action1Active);
              return (
                <div
                  key={i}
                  className="relative bg-white rounded-xl p-3 border shadow-sm transition-all"
                  style={{
                    borderColor: isActive ? action.color : '#E5E7EB',
                    borderWidth: isActive ? '2px' : '1px',
                  }}
                  data-testid={`mobile-action-${i}`}
                >
                  <div className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: action.color }}>
                    {action.role}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] shrink-0"
                      style={{ borderLeftColor: action.color }}
                    />
                    <span className="text-xs font-semibold text-gray-900">{action.label}</span>
                  </div>
                  <div
                    className="mt-2 inline-block px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wide"
                    style={{
                      backgroundColor: `${action.color}20`,
                      color: action.color,
                    }}
                  >
                    Do it for me
                  </div>
                </div>
              );
            })}
          </div>

          {/* Down chevron */}
          <div className="flex flex-col items-center">
            <div className="w-px h-6 bg-gradient-to-b from-gray-300 to-emerald-400" />
            <div className="w-2 h-2 rotate-45 border-r-2 border-b-2 border-emerald-500 -mt-1" />
          </div>

          {/* CRM rotating badge */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center overflow-hidden">
                <div className="transition-opacity duration-500" style={{ opacity: currentCrm.crm === 'Salesforce' ? 1 : 0, position: 'absolute' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24">
                    <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
                  </svg>
                </div>
                <div className="transition-opacity duration-500" style={{ opacity: currentCrm.crm === 'Jira' ? 1 : 0, position: 'absolute' }}>
                  <img src="/assets/sbre7usi.png" alt="Jira" className="w-6 h-6 object-contain" />
                </div>
              </div>
              <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg width="8" height="8" viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            </div>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Auto-syncs to your CRM</span>
          </div>
        </div>
      </div>

      {/* ═══════ DESKTOP: full SVG animation (≥ md) ═══════ */}
      <div className="relative bg-transparent px-4 sm:px-6 hidden md:block">

        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto" style={{ minHeight: '160px' }}>
          <defs>
            <filter id="cardShadow">
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.06" />
            </filter>
          </defs>

          {/* ── LEFT: Dot streams from tools to center ── */}
          {sources.map((src, i) => (
            <DotStream
              key={`dots-l-${i}`}
              fromX={src.x} fromY={src.y}
              toX={centerX} toY={centerY}
              active={toolsActive}
              color="#6366F1"
              count={4}
            />
          ))}

          {/* ── MIDDLE: Dots from center to action card 0 (straight horizontal then straight up) ── */}
          <DotStream
            fromX={centerX} fromY={centerY}
            toX={outCardX - 4} toY={actionYs[0]}
            active={action0Active}
            color={actions[0].color}
            count={4}
          />

          {/* ── MIDDLE: Dots from center to action card 1 (straight horizontal then straight down) ── */}
          <DotStream
            fromX={centerX} fromY={centerY}
            toX={outCardX - 4} toY={actionYs[1]}
            active={action1Active}
            color={actions[1].color}
            count={4}
          />

          {/* ── RIGHT: Dots from action 0 to CRM ── */}
          <DotStream
            fromX={outCardX + 160} fromY={actionYs[0]}
            toX={crmX - 8} toY={centerY}
            active={crm0Active}
            color={actions[0].color}
            count={4}
          />

          {/* ── RIGHT: Dots from action 1 to CRM ── */}
          <DotStream
            fromX={outCardX + 160} fromY={actionYs[1]}
            toX={crmX - 8} toY={centerY}
            active={crm1Active}
            color={actions[1].color}
            count={4}
          />

          {/* ── LEFT: Tool logos, SVG circles removed, HTML overlays are the circles ── */}
          {sources.map((src, i) => {
            const LogoComp = logoComponents[i];
            return (
              <g key={`src-${i}`}>
                <LogoComp x={src.x} y={src.y} size={0} />
              </g>
            );
          })}

          {/* ── MIDDLE: Action cards (always visible, glow when active) ── */}
          {actions.map((action, i) => {
            const ay = actionYs[i];
            const isActive = (i === 0 && action0Active) || (i === 1 && action1Active);
            return (
              <g key={`action-${i}`}>
                {/* Role label */}
                <text x={outCardX} y={ay - 14} fill={action.color} fontSize="8" fontWeight="800" letterSpacing="0.8" fontFamily="system-ui, sans-serif">
                  {action.role.toUpperCase()}
                </text>
                {/* Card */}
                <rect
                  x={outCardX} y={ay - 10} width="160" height="24" rx="6"
                  fill="white"
                  stroke={isActive ? action.color : '#E5E7EB'}
                  strokeWidth={isActive ? '2' : '1'}
                  filter="url(#cardShadow)"
                  style={{ transition: 'stroke 0.3s, stroke-width 0.3s' }}
                />
                {/* Arrow */}
                <polygon points={`${outCardX + 9},${ay - 3} ${outCardX + 14},${ay + 2} ${outCardX + 9},${ay + 7}`} fill={action.color} />
                {/* Label */}
                <text x={outCardX + 20} y={ay + 2} dominantBaseline="central" fill="#111827" fontSize="9" fontWeight="700" fontFamily="system-ui, sans-serif">
                  {action.label}
                </text>
                {/* "Do it for me" pill below */}
                <rect x={outCardX + 2} y={ay + 18} width="52" height="15" rx="7" fill={action.color} opacity={isActive ? 0.15 : 0.08} style={{ transition: 'opacity 0.3s' }} />
                <text x={outCardX + 28} y={ay + 26} textAnchor="middle" dominantBaseline="central" fill={action.color} fontSize="6.5" fontWeight="800" fontFamily="system-ui, sans-serif">
                  Do it for me
                </text>
              </g>
            );
          })}

          {/* ── LABELS removed per user request ── */}
        </svg>

        {/* HTML overlays for logos */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Salesforce, row 1 left */}
          <div className="absolute" style={{ left: '14.06%', top: '27.8%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24">
                <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
              </svg>
            </div>
          </div>
          {/* Gong, row 1 right */}
          <div className="absolute" style={{ left: '22.56%', top: '27.8%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
              <img src="/assets/dcxyztic.png" alt="Gong" className="w-7 h-7 object-contain" />
            </div>
          </div>
          {/* Gmail, row 2 left */}
          <div className="absolute" style={{ left: '14.06%', top: '53.7%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
              <img src="/assets/0hye7w5y.png" alt="Gmail" className="w-7 h-7 object-contain" />
            </div>
          </div>
          {/* Slack, row 2 right */}
          <div className="absolute" style={{ left: '22.56%', top: '53.7%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
              <img src="/assets/yi9s0mbf.webp" alt="Slack" className="w-7 h-7 object-contain" />
            </div>
          </div>
          {/* Jira, row 3 center */}
          <div className="absolute" style={{ left: '18.26%', top: '77.8%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
              <img src="/assets/sbre7usi.png" alt="Jira" className="w-7 h-7 object-contain" />
            </div>
          </div>
          {/* Forsyt center */}
          <div className="absolute" style={{ left: '39.51%', top: '50%', transform: 'translate(-50%, -50%)' }}>
            <div className="absolute inset-0 w-20 h-20 -m-2 rounded-full border border-gray-200/40" style={{ animation: 'pulse-ring 3s ease-in-out infinite' }} />
            <div className="absolute inset-0 w-24 h-24 -m-4 rounded-full border border-gray-200/20" style={{ animation: 'pulse-ring 4s ease-in-out infinite 0.5s' }} />
            <div className="w-16 h-16 rounded-full bg-white shadow-lg overflow-hidden flex items-center justify-center">
              <img
                src="/assets/dcztovt1.png"
                alt="Forsyt"
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Rotating CRM badge */}
        <div className="absolute pointer-events-none" style={{ left: '86.06%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          <div className="flex flex-col items-center">
            <div className="relative w-12 h-12">
              <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center overflow-hidden">
                <div className="transition-opacity duration-500" style={{ opacity: currentCrm.crm === 'Salesforce' ? 1 : 0, position: 'absolute' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24">
                    <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
                  </svg>
                </div>
                <div className="transition-opacity duration-500" style={{ opacity: currentCrm.crm === 'Jira' ? 1 : 0, position: 'absolute' }}>
                  <img src="/assets/sbre7usi.png" alt="Jira" className="w-7 h-7 object-contain" />
                </div>
              </div>
              <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg width="8" height="8" viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-4" />
    </div>
  );
};

export default DataFlowAnimation;
